/**
 * MCP server registry — list / add / remove servers and inspect their
 * discovered tool catalog. Re-skinned to the neuro surface in PR-A3;
 * the underlying API + form components are untouched.
 */
import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createResource,
  createSignal,
} from "solid-js";

import type { McpServerSpec, McpToolView } from "@/api/generated";
import { deleteMcpServer, listMcpServers, listMcpTools } from "@/api/mcp";

import AccessPanel from "./AccessPanel";
import AddMcpServerForm from "./AddMcpServerForm";

const McpSettingsPage: Component = () => {
  const [servers, { mutate: setServers, refetch: refetchServers }] =
    createResource<McpServerSpec[]>(listMcpServers);
  const [tools, { refetch: refetchTools }] =
    createResource<McpToolView[]>(listMcpTools);
  const [removing, setRemoving] = createSignal<string | null>(null);
  const [removeError, setRemoveError] = createSignal<string | null>(null);

  const onCreated = (server: McpServerSpec) => {
    setServers((prev) => (prev ? [...prev, server] : [server]));
    void refetchTools();
  };

  const onDelete = async (id: string) => {
    if (removing()) return;
    setRemoving(id);
    setRemoveError(null);
    try {
      await deleteMcpServer(id);
      setServers((prev) => prev?.filter((s) => s.id !== id));
      void refetchTools();
    } catch (err) {
      const apiErr = err as { message?: string };
      setRemoveError(apiErr.message ?? String(err));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div class="neuro-page">
      <div class="neuro-surface">
        <div class="neuro-route-body">
          <h2>Settings</h2>

          <h3>Access</h3>
          <AccessPanel />

          <h3>MCP servers</h3>
          <p>Register stdio or SSE MCP servers so chat can call their tools.</p>

          <Show when={!servers.loading} fallback={<p>loading servers…</p>}>
            <Show when={servers.error}>
              <div class="neuro-error-block">
                <span class="neuro-error-title">failed to load servers</span>
                <span class="neuro-error-detail">
                  {(servers.error as { message?: string })?.message ??
                    String(servers.error)}
                </span>
              </div>
            </Show>
            <Show
              when={(servers() ?? []).length > 0}
              fallback={<p>no servers registered yet.</p>}
            >
              <table class="neuro-table">
                <thead>
                  <tr>
                    <th>name</th>
                    <th>kind</th>
                    <th>status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={servers() ?? []}>
                    {(s) => (
                      <tr>
                        <td>
                          <code>{s.name}</code>
                          <Show when={s.last_error}>
                            {(err) => (
                              <>
                                <br />
                                <span
                                  class="num"
                                  style="color: var(--text-tertiary); font-size: 11px;"
                                >
                                  err: {err()}
                                </span>
                              </>
                            )}
                          </Show>
                        </td>
                        <td class="muted">
                          <Switch>
                            <Match when={s.kind === "stdio"}>
                              stdio · <code>{s.command}</code>
                            </Match>
                            <Match when={s.kind === "sse"}>
                              sse · <code>{s.url}</code>
                            </Match>
                          </Switch>
                        </td>
                        <td class="muted">{s.status}</td>
                        <td>
                          <button
                            type="button"
                            class="neuro-action subtle"
                            onClick={() => void onDelete(s.id)}
                            disabled={removing() === s.id}
                          >
                            {removing() === s.id ? "removing…" : "remove"}
                          </button>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </Show>
            <Show when={removeError()}>
              {(msg) => (
                <div class="neuro-error-block">
                  <span class="neuro-error-title">remove failed</span>
                  <span class="neuro-error-detail">{msg()}</span>
                </div>
              )}
            </Show>
          </Show>

          <h3>register a server</h3>
          <AddMcpServerForm onCreated={onCreated} />

          <h3>
            tool catalog
            <button
              type="button"
              class="neuro-action subtle"
              style="margin-left: 8px;"
              onClick={() => {
                void refetchServers();
                void refetchTools();
              }}
            >
              refresh
            </button>
          </h3>
          <p>Tools discovered across all connected servers.</p>

          <Show when={!tools.loading} fallback={<p>loading tools…</p>}>
            <Show
              when={(tools() ?? []).length > 0}
              fallback={<p>no tools available yet.</p>}
            >
              <table class="neuro-table">
                <thead>
                  <tr>
                    <th>name</th>
                    <th>description</th>
                    <th>server</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={tools() ?? []}>
                    {(t) => (
                      <tr>
                        <td>
                          <code>{t.name}</code>
                        </td>
                        <td class="muted">{t.description ?? "—"}</td>
                        <td class="muted">
                          <code>{shortId(t.server_id)}</code>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </Show>
          </Show>
        </div>
      </div>
    </div>
  );
};

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export default McpSettingsPage;
